<script setup>
const {  isMobile } = useDevice();
import { ref, onMounted, onBeforeUnmount } from "vue";
// PrimeVue components
const Avatar = defineAsyncComponent(() => import('primevue/avatar'))
const AvatarGroup = defineAsyncComponent(() => import('primevue/avatargroup'))

// Local components
const WrContactFormPopup = defineAsyncComponent(() => import('~/components/molecules/WrContactFormPopup.vue'))
const WrProjectSection = defineAsyncComponent(() => import('~/components/molecules/WrProjectSection.vue'))
const WrResultSection = defineAsyncComponent(() => import('~/components/molecules/WrResultSection.vue'))


const url = useRequestURL();
const { data, error } = await useFetch(
  `${url.origin}/content/testimonials-content.json`
);

if (error.value || !data.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Page not found",
  });
}

useHead({
  title: data.value?.meta_tags?.title || "",
  meta: data.value?.meta_tags?.meta || [],
});

const visible = ref(false);

const openModal = () => {
  visible.value = true;
};


const testimonialHeading = ref(null);

const scrollToSection = () => {
  const section = testimonialHeading.value;
  if (section) {
    const yOffset = -80;
    const y =
      section.getBoundingClientRect().top + window.pageYOffset + yOffset;

    window.scrollTo({
      top: y,
      behavior: "smooth",
    });
  }
};



const ytVideosCards = [...data.value?.ytVideosCards];
const categories = ref([...data.value?.clients_Tesimonials?.testimonials.slice(0 ,3)]);

const appendedCategories = ref([...data.value?.clients_Tesimonials?.testimonials]);

const handleAppend = () => {
  categories.value = [...appendedCategories.value];
  nextTick(() => {
    scrollToSection();
  });
};

const handleRemove = () => {
  categories.value = categories.value.slice(0, 3);
  nextTick(() => {
    scrollToSection();
  });
};
</script>
<template>
  <div class="max-w-screen-xl mx-auto px-4 relative">
    <span
      class="hidden dark:block size-[1421px] rounded-full bg-[linear-gradient(180deg,_#47116B_41.7%,_rgba(137,22,212,0)_100%)] blur-[200px] absolute -top-[648px] left-[155px] opacity-90"
    />
    <span
      class="dark:hidden h-[1004px] w-[1067px] rounded-full bg-[#FFFFFF80] blur-[200px] absolute top-0 -left-[256px] opacity-90"
    />

    <!-- Section 1: Intro Section -->
    <section class="relative z-10">
      <div class="rising-stars top-0">
        <div style="height: 4px; width: 4px"></div>
        <div style="height: 3px; width: 3px"></div>
        <div style="height: 2px; width: 2px"></div>
        <div style="height: 1px; width: 1px"></div>
      </div>
      <div class="xl:mb-[82px] mb-8 relative z-10">
        <h1 class="mb-2 max-md:mb-4 md:mb-4">
          <WrGradientText
            :text="data?.header?.title"
            append-class="pb-0 lg:pb-2 bg-clip-text bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_2.57%,_var(--text-gradient-2)_35.72%)]"
          />
          Your appreciation turns our work into purpose.
        </h1>
        <p class="text-2xl max-md:text-xl tracking-[0.5px]">
          {{data.header.subtitle}}
        </p>
      </div>

      <div
        class="flex max-xl:flex-col xl:justify-between xl:items-center max-xl:justify-start max-xl:items-start max-xl:gap-4"
      >
        <div
          class="w-[65%] flex xl:flex-col max-md:flex-col max-xl:gap-2 max-md:gap-0 max-xl:items-baseline max-md:w-full relative z-10"
        >
          <div class="rising-stars top-0">
            <div style="height: 4px; width: 4px" q:key="0"></div>
            <div style="height: 3px; width: 3px" q:key="1"></div>
            <div style="height: 2px; width: 2px" q:key="2"></div>
            <div style="height: 1px; width: 1px" q:key="3"></div>
          </div>
          <h2
            class="xl:text-[86px] lg:text-5xl text-3xl md:text-4xl  font-semibold"
          >
           {{data?.header?.project?.count}}
          </h2>
          <p class="text-xl md:mt-2"> {{data?.header?.project?.text}}</p>
        </div>

        <div class="flex flex-col gap-7 relative z-10">
          <div class="flex items-center gap-12 max-sm:flex-col max-sm:gap-y-2">
            <div class="card flex justify-center max-md:my-4">
              <AvatarGroup>
                <template v-for="avatar in data?.header?.avatarImages">
                  <Avatar
                    :pt="{
                      image: {
                        alt: 'Avatar Image',
                      },
                    }"
                    :image="avatar.image"
                    shape="circle"
                    :size="isMobile ? 'large' : 'small'"
                    class="!border-[3px] shadow-[-10px_4px_4px_0px_#00000040] !bg-[#d9d9d9]"
                  />
                </template>
              </AvatarGroup>
            </div>
            <WrButton class="w-full" :appendClass="'w-full'" @click="openModal"
              >{{data.header.button.text}}</WrButton
            >
          </div>
          <span
            class="text-2xl max-md:text-xl max-md:pr-2 pr-4 max-xl:pr-10 tracking-[0.5px]"
          >
          {{ data.header.introText }}
          </span>
        </div>
      </div>
    </section>
    <div
      class="overflow-x-auto overflow-y-hidden w-full xl:mt-28 mt-20 scroll-smooth styled-scrollbar"
    >
      <div class="flex gap-6 w-max relative z-10">
        <template v-for="card in ytVideosCards" :key="card.name">
          <NuxtLink :to="card.link" target="_blank">
            <div
              class="p-[1px] min-w-[475px] max-w-[475px] min-h-max !max-h-max !h-full bg-[image:var(--border-ytvideos-card)] rounded-xl"
            >
              <div class="bg-[rgba(164,98,196,1)] rounded-xl">
                <div
                  class="relative bg-[image:var(--bg-ytvideos-cards)] rounded-xl text-white overflow-hidden"
                >
                  <NuxtImg
                    class="rounded-t-xl opacity-60 dark:opacity-40"
                    :alt="card.company"
                    :src="card.image"
                    height="260"
                    width="475"
                    draggable="false"
                    loading="eager"
                    format="webp"
                  />
                  <NuxtImg
                    alt="Video Player button Img"
                    src="/images/videoBtn.webp"
                    class="absolute top-[40%] left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                    height="108"
                    width="108"
                    draggable="false"
                    loading="lazy"
                    format="webp"
                  />
                  <div
                    class="flex flex-col items-start justify-start pt-4 pl-16 px-5 pb-5 tracking-[0.5px]"
                  >
                    <p class="text-xl font-semibold">{{ card.name }}</p>
                    <p class="text-xl">{{ card.company }}</p>
                  </div>
                </div>
              </div>
            </div>
          </NuxtLink>
        </template>
      </div>
    </div>
  </div>

  <!-- Section 2: Project Section -->
  <section class="relative z-10">
    <WrProjectSection class="relative z-10" />
  </section>

  <div
    class="max-w-screen-xl mx-auto px-4 xl:mt-28 mt-10 relative z-10"
  >
    <!-- Section 3: Cards Section -->

    <section
      ref="testimonialHeading"
      id="testimonials-heading"
      class="lg:px-2 relative"
    >
      <span
        class="dark:hidden h-[304px] w-[1067px] rounded-full bg-[#FFFFFF80] blur-[200px] absolute -top-10 -left-[256px] opacity-90"
      />
      <h3 class="xl:text-center relative z-10">
        <span
          class="dark:hidden h-[167px] w-[1268px] rounded-full bg-[rgba(150,90,191,0.7)] blur-[300px] absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        />
        <WrGradientText
          :text="data?.clients_Tesimonials?.heading"
          append-class="bg-clip-text bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_25.57%,_var(--text-gradient-2)_65.72%)]"
        />
      </h3>
      <p class="text-xl xl:text-center relative z-10">
      {{ data?.clients_Tesimonials?.text }}
      </p>
      <!-- <WrTestimonialCardCarousel
        class="mt-14 mb-12 relative z-10"
        v-if="isMobile || isTablet"
        :content="categories"
      /> -->
      <div>
        <WrCards class="mt-14 mb-12 relative z-10" :content="categories" />

        <div class="flex items-center justify-center w-full relative z-10">
          <WrButton @click="handleAppend" v-if="categories.length < 5"
            >Expand more</WrButton
          >
          <WrButton @click="handleRemove" v-else>Collapse</WrButton>
        </div>
      </div>
    </section>

    <!-- Section 4: Result Section -->

    <section class="my-10 sm:my-20 relative z-10">
      <WrResultSection />
    </section>
  </div>
  <WrContactFormPopup :isOpen="visible" @close="visible = false" class="z-30" />
</template>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
